import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { toast } from "react-toastify";
import { TextInput, SelectInput, CountrySelectDropdown, FormContainer } from "../../../shared";
import { Pattern, AxiosGraphQlAction, APPLICATION_LIST } from "../../../services";
import { getVeevaSpecilaty, getContentCategory, findDuplicate, AUDIENCE_TYPE } from "./service";
import { type IPersona } from "./types";
import { getPersonaList, setPersonaList } from "../../reducers";
import { connect } from "react-redux";
import { ERROR_CODE_LIST as codeList } from "../../../../utils/errCodes";

function Form(props: any) {
    const { formType, query, formData } = props;

    const defaultValues =
        formType === "update"
            ? {
                  ...formData,
              }
            : {};

    const [loading, setLoading] = useState(false);

    const [country, setCountry] = useState(formType === "update" ? formData.country : "");
    const [duplicateAlert, setDuplicateAlert] = useState(false);
    const [contentCategories, setContentCategories] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [currentContentCategory, setCurrentContentCategory] = useState("");
    const [shouldCheckOptions, setShouldCheckOptions] = useState(false);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        reset,
        resetField,
        getValues,
        setValue,
        setError,
        clearErrors,
    } = useForm({
        defaultValues,
    });

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentContentCategory(e.target.value);
        setValue("specialty", "");
    };

    const handleAddCategory = () => {
        if (!getValues("country")) {
            setError(`country`, {});
        } else {
            let appendEntry = true;
            fields.map((field: any) => {
                if (
                    field?.audienceType === getValues("type") &&
                    field?.personaKey === getValues("specialty")
                ) {
                    appendEntry = false;
                    setDuplicateAlert(true);
                    setTimeout(() => {
                        setDuplicateAlert(false);
                    }, 2000);
                }
                return field;
            });

            let personaText: string | any;

            if (getValues("specialty")) {
                getValues("type") === "content-category"
                    ? (personaText = contentCategories.find(
                          (item: any) => item.value === getValues("specialty")
                      ))
                    : (personaText = specialties.find(
                          (item: any) => item.value === getValues("specialty")
                      ));
                personaText = personaText.text;
            }

            let appendObj;

            if (appendEntry && getValues("type") && getValues("specialty")) {
                appendObj = {
                    personaKey: getValues("specialty"),
                    personaText,
                    audienceType: getValues("type"),
                };
                append(appendObj);
            }

            if (appendEntry && getValues("type") && getValues("specialty")) {
                resetField("type");
                resetField("specialty");
            }
            return {
                appendEntry,
                data: appendObj,
            };
        }
    };

    const handleCountryChange = (val: string) => {
        setCountry(val);
        replace([]);
        clearErrors("country");
        resetField("type");
        resetField("specialty");
    };

    useEffect((): any => {
        let isApiCalled = true;
        setLoading(true);
        getVeevaSpecilaty(country)
            .then((response: any) => {
                if (isApiCalled) {
                    setSpecialties(response);
                    setLoading(false);
                }
                setShouldCheckOptions(true);
            })
            .catch((err) => {
                setSpecialties([]);
                setLoading(false);
                setShouldCheckOptions(true);
                toast.error(err.message);
            });
        return () => (isApiCalled = false);
    }, [country]);

    useEffect((): any => {
        let isApiCalled = true;
        setLoading(true);
        getContentCategory()
            .then((response) => {
                isApiCalled && setContentCategories(response);
            })
            .catch((err) => {
                throw new Error(err);
            });

        return () => (isApiCalled = false);
    }, []);

    const { fields, update, append, remove, replace } = useFieldArray({
        name: "audienceSpecialty",
        control,
    });
    const scrollRef = useRef(null);

    const onFormSubmit = handleSubmit((data) => {
        const appendData = handleAddCategory();
        appendData?.data && data.audienceSpecialty.push(appendData?.data);
        const CHECK_VALUE = appendData?.appendEntry;

        if (CHECK_VALUE) {
            const index = findDuplicate(data.audienceSpecialty);

            if (index) {
                setError(`audienceSpecialty.${index}.audienceType`, {});
                setError(`audienceSpecialty.${index}.personaKey`, {});
            } else {
                setLoading(true);
                const currentElement: any = scrollRef.current;
                window.scrollTo({
                    top: currentElement.offsetTop,
                    behavior: "smooth",
                });
                const variables =
                    formType === "update"
                        ? {
                              id: data.id,
                              abbvieProPersona: {
                                  personaName: data.personaName,
                                  country: data.country,
                                  appName: data.appName,
                                  audienceSpecialty: data.audienceSpecialty,
                              },
                          }
                        : {
                              abbvieProPersona: {
                                  personaName: data.personaName,
                                  country: data.country,
                                  appName: data.appName,
                                  personaType: "Audience",
                                  audienceSpecialty: data.audienceSpecialty.map((item: any) => {
                                      return {
                                          audienceType: item.audienceType,
                                          personaKey: item.personaKey,
                                          personaText: item.personaText,
                                      };
                                  }),
                              },
                          };

                AxiosGraphQlAction(variables, query).then((response) => {
                    if (response.data.errors) {
                        const errMgs =
                            // @ts-expect-error: Unreachable code error
                            codeList[response.data.errors[0].errcode] ||
                            "Something went wrong. Please try again.";

                        toast.error(errMgs);
                    } else {
                        if (formType !== "update") {
                            reset();
                            setSpecialties([]);
                            replace([]);
                            resetField("type");
                            resetField("specialty");
                        }

                        toast.success(
                            `Successfully ${formType === "update" ? "Updated" : "Created"}`
                        );

                        if (formType === "update") {
                            const _personaList: IPersona[] = [];
                            props.personaAudience.forEach((item: IPersona) => {
                                let _obj = { ...item };
                                if (item.id === variables.id) {
                                    _obj = {
                                        ..._obj,
                                        personaName: data.personaName,
                                        country: data.country,
                                        appName: data.appName,
                                        audienceSpecialty: [...data.audienceSpecialty],
                                    };
                                    _personaList.push(_obj);
                                } else {
                                    _personaList.push(_obj);
                                }
                            });

                            props.setPersonaList(_personaList);
                        }
                    }
                    setLoading(false);
                });
            }
        }
    });

    return (
        <FormContainer
            submitAction={onFormSubmit}
            submitButtonText={formType === "update" ? "Update Persona" : "Create Persona"}
            noValidate
            loading={loading}
            ref={scrollRef}
        >
            <div className="row">
                <div className="col-12">
                    <TextInput
                        id="personaName"
                        type="text"
                        labelText="Persona Name"
                        {...register("personaName", {
                            required: true,
                            pattern: Pattern.name,
                        })}
                        isInvalid={!!errors.personaName}
                        required
                    />
                </div>

                <div className="col-12">
                    <SelectInput
                        id="appName"
                        labelText="Application Name"
                        {...register("appName", {
                            required: true,
                        })}
                        options={APPLICATION_LIST}
                        isInvalid={!!errors.appName}
                        required
                    />
                </div>

                <div className="col-12">
                    <Controller
                        name="country"
                        render={({ field: { name, onChange, value } }) => (
                            <CountrySelectDropdown
                                id="country"
                                country={value}
                                labelText="Country"
                                selectCountry={(val) => {
                                    onChange(val);
                                    handleCountryChange(val);
                                }}
                                {...register("country", {
                                    required: true,
                                })}
                                required
                                isInvalid={!!errors.country}
                            />
                        )}
                        control={control}
                    />
                </div>
            </div>

            <div className="bg-light border p-4 mb-4">
                <div className="form-multifrield form-group mb-3">
                    {shouldCheckOptions &&
                        fields.map((field: any, index: number) => {
                            return (
                                <div className="row" key={field.id}>
                                    <div className="col">
                                        <SelectInput
                                            {...register(
                                                `audienceSpecialty.${index}.audienceType`,
                                                {
                                                    required: true,
                                                }
                                            )}
                                            labelText="Audience Type"
                                            // @ts-expect-error: Unreachable code error
                                            defaultValue={field.audienceType}
                                            options={AUDIENCE_TYPE}
                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                setValue(
                                                    `audienceSpecialty.${index}.audienceType`,
                                                    e.target.value,
                                                    {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    }
                                                );

                                                update(index, {
                                                    audienceType: e.target.value,
                                                    personaKey: "",
                                                });
                                            }}
                                            required
                                            isInvalid={
                                                !!(
                                                    errors.audienceSpecialty &&
                                                    // @ts-expect-error: Unreachable code error
                                                    errors?.audienceSpecialty[index]?.audienceType
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="col">
                                        <SelectInput
                                            {...register(
                                                `audienceSpecialty.${index}.personaKey` as const,
                                                {
                                                    required: true,
                                                }
                                            )}
                                            labelText="Specialty"
                                            // @ts-expect-error: Unreachable code error
                                            defaultValue={field.personaKey}
                                            options={
                                                field?.audienceType === "content-category"
                                                    ? contentCategories
                                                    : field?.audienceType === "specialty"
                                                      ? specialties
                                                      : []
                                            }
                                            isInvalid={
                                                !!(
                                                    errors.audienceSpecialty &&
                                                    // @ts-expect-error: Unreachable code error
                                                    errors?.audienceSpecialty[index]?.personaKey
                                                )
                                            }
                                            required
                                        />
                                    </div>
                                    <div className="col-auto mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger px-4 mt-3"
                                            onClick={() => {
                                                remove(index);
                                            }}
                                        >
                                            X
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                </div>
                <div className="row">
                    <div className="col-lg">
                        <SelectInput
                            labelText="Audience Type"
                            {...register(`type`, {
                                required: !(fields.length > 0),
                            })}
                            options={AUDIENCE_TYPE}
                            required
                            isInvalid={!!errors.type}
                            // @ts-expect-error: Unreachable code error
                            onChange={(e) => {
                                handleCategoryChange(e);
                            }}
                        />
                    </div>
                    <div className="col-lg">
                        <SelectInput
                            labelText="Specialty"
                            {...register("specialty", {
                                required: !(fields.length > 0),
                            })}
                            options={
                                currentContentCategory === "content-category"
                                    ? contentCategories
                                    : currentContentCategory === "specialty"
                                      ? specialties
                                      : []
                            }
                            required
                            isInvalid={!!errors.specialty}
                        />
                    </div>
                    <div className="col-auto pt-3">
                        <button
                            type="button"
                            className="btn btn-outline-info mt-4"
                            onClick={handleAddCategory}
                        >
                            Add
                        </button>
                    </div>

                    {duplicateAlert && (
                        <div className="col-12">
                            <p className="mb-0 text-danger small">
                                Duplicate Audience Type and Speciality.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </FormContainer>
    );
}

const mapStateToProps = (state: any) => {
    return {
        personaAudience: getPersonaList(state),
        user: state.users.currentUser,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        setPersonaList: (personaList: any) => {
            dispatch(setPersonaList(personaList));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Form);
